/**
 * Schwebende Tab-Leiste für Telefone.
 *
 * Muster aus `apple-struktur.md`: eine Kapsel über dem Inhalt, der aktive
 * Bereich mit Symbol und Text, die übrigen nur als Symbol — anders passen
 * fünf Bereiche nicht auf ein Telefon. Beim Herunterscrollen minimiert sich
 * die Leiste und gibt den Inhalt frei, beim Hochscrollen kommt sie zurück.
 *
 * Die Beschriftung bleibt für die Vorlesehilfe immer vorhanden (`sr-only`),
 * auch wenn sie optisch entfällt.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { UserRole } from '@/types';
import { bereicheFuerRolle, bereichFuerPfad } from '@/lib/navigation';

export interface MobileTabBarProps {
  /** Rolle des angemeldeten Nutzers — wie bei der Sidebar, rein kosmetisch. */
  rolle?: UserRole;
}

export function MobileTabBar({ rolle }: MobileTabBarProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const aktiv = bereichFuerPfad(pathname);

  const [minimiert, setMinimiert] = useState(false);
  const letztePosition = useRef(0);

  useEffect(() => {
    function beiScroll() {
      const jetzt = window.scrollY;
      // Kleine Bewegungen ignorieren, sonst flackert die Leiste beim Tippen.
      if (Math.abs(jetzt - letztePosition.current) > 8) {
        setMinimiert(jetzt > letztePosition.current && jetzt > 64);
        letztePosition.current = jetzt;
      }
    }

    window.addEventListener('scroll', beiScroll, { passive: true });
    return () => window.removeEventListener('scroll', beiScroll);
  }, []);

  return (
    <div
      data-minimiert={String(minimiert)}
      className={[
        'fixed bottom-4 left-1/2 z-40 -translate-x-1/2 md:hidden',
        'flex items-center gap-1 rounded-full border border-surface-300/60',
        'bg-surface-100/85 p-1.5 shadow-lg backdrop-blur-xl',
        'transition-all duration-300',
        minimiert ? 'translate-y-24 opacity-0' : 'translate-y-0 opacity-100',
      ].join(' ')}
    >
      {bereicheFuerRolle(rolle).map((bereich) => {
        const istAktiv = aktiv?.id === bereich.id;
        const Icon = bereich.icon;
        const label = t(bereich.labelKey);

        return (
          <Link
            key={bereich.id}
            to={bereich.href}
            aria-current={istAktiv ? 'page' : undefined}
            className={[
              'flex items-center gap-2 rounded-full transition-colors',
              // Mindestens 44 px Trefferfläche — Touch-Regel.
              'min-h-11 px-3',
              istAktiv
                ? 'bg-accent font-semibold text-[color:var(--color-on-accent)]'
                : 'text-surface-600',
            ].join(' ')}
          >
            <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span className={istAktiv ? 'text-sm' : 'sr-only'}>{label}</span>
          </Link>
        );
      })}
    </div>
  );
}

export default MobileTabBar;
